import { memo, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';

import useAuthContext from '@/features/auth/_shared/contexts/auth.context';
import { Role } from '@/features/user/types/user.type';
import { Spinner } from '@/components/_ui/spinner';

interface RoleHandlerRouteProps {
  children: React.ReactNode;
  roles: Role['name'][];
}

/**
 * RoleHandlerRoute - Production-grade route guard component
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Optimized role checking with useMemo
 * - Proper null/undefined safety
 * - No memory leaks (React Router handles cleanup)
 */
const RoleHandlerRoute = memo(({ children, roles }: RoleHandlerRouteProps) => {
  const { isAuthenticated, state } = useAuthContext();
  const user = state.user;
  const location = useLocation();

  // Memoize role check to prevent unnecessary recalculations
  const hasRequiredRole = useMemo(() => {
    const userRole = user?.role?.name;
    if (!userRole) return false;
    return roles.includes(userRole as Role['name']);
  }, [user?.role?.name, roles]);

  // Wait for hydration before deciding
  // if (!hydrated || state?.loading) {
  //   return (
  //     <div className="flex justify-center items-center flex-1 h-screen">
  //       <Spinner className="h-[40px] w-[40px]" />
  //     </div>
  //   );
  // }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (!hasRequiredRole) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
});

RoleHandlerRoute.displayName = 'RoleHandlerRoute';

/**
 * FirstRedirectRoute - Production-grade initial redirect component
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Uses context's isTicketingRole (already memoized) instead of recalculating
 * - Proper null/undefined safety
 * - No memory leaks (React Router handles cleanup)
 */
const FirstRedirectRoute = memo(() => {
  const { isAuthenticated, isTicketingRole, isExternalUserRole } = useAuthContext();
  const location = useLocation();

  // Memoize redirect path to prevent unnecessary recalculations
  const redirectPath = useMemo(() => {
    if (isExternalUserRole) {
      return '/unassigned-cases';
    }
    return isTicketingRole ? '/ticketing' : '/active-cases';
  }, [isTicketingRole]);

  // Wait for hydration before deciding
  // if (!hydrated || state?.loading) {
  //   return (
  //     <div className="flex justify-center items-center flex-1 h-screen">
  //       <Spinner className="h-[40px] w-[40px]" />
  //     </div>
  //   );
  // }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Navigate to={redirectPath} state={{ from: location }} replace />;
});

FirstRedirectRoute.displayName = 'FirstRedirectRoute';

export { RoleHandlerRoute, FirstRedirectRoute };
